import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getPools, createPool, deletePool } from '../api';

const WORKER_URL = 'https://songbattle-worker.richard-heiland6.workers.dev';
const API_BASE = process.env.REACT_APP_API_URL || (
  process.env.NODE_ENV === 'production' ? `${WORKER_URL}/api` : 'http://localhost:8787/api'
);

function Import() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [accessToken, setAccessToken] = useState(null);
  const [playlistUrls, setPlaylistUrls] = useState('');
  const [artistNames, setArtistNames] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [userInfo, setUserInfo] = useState(null);

  // Collections ("Sammlungen")
  const [pools, setPools] = useState([]);
  const [selectedPoolId, setSelectedPoolId] = useState('');
  const [newPoolName, setNewPoolName] = useState('');

  const loadPools = () => getPools().then(data => setPools(data.pools || [])).catch(() => {});

  useEffect(() => {
    loadPools();
  }, []);

  useEffect(() => {
    // Check if we got a token from callback
    const token = searchParams.get('access_token');
    if (token) {
      setAccessToken(token);
      // Test token immediately
      fetch(`${API_BASE}/spotify/test-token?token=${token}`)
        .then(r => r.json())
        .then(data => {
          if (data.error) {
            setError(`Token-Test fehlgeschlagen: ${data.error} - ${JSON.stringify(data.message)}`);
          } else {
            setUserInfo(data);
          }
        })
        .catch(() => setError('Token-Test fehlgeschlagen'));
      // Clean URL
      window.history.replaceState({}, '', '/import');
    }
  }, [searchParams]);

  const handleLogin = async () => {
    try {
      const res = await fetch(`${API_BASE}/spotify/login`);
      const data = await res.json();
      window.location.href = data.url;
    } catch (err) {
      setError('Login fehlgeschlagen');
    }
  };

  const handleCreatePool = async () => {
    const name = newPoolName.trim();
    if (!name) return;
    try {
      const data = await createPool(name);
      setNewPoolName('');
      await loadPools();
      setSelectedPoolId(data.pool.id);
    } catch (err) {
      setError('Sammlung konnte nicht erstellt werden.');
    }
  };

  const handleDeletePool = async () => {
    if (!selectedPoolId) return;
    const pool = pools.find(p => p.id === selectedPoolId);
    if (!window.confirm(`Sammlung "${pool?.name}" mit ${pool?.count || 0} Songs wirklich löschen?`)) return;
    try {
      await deletePool(selectedPoolId);
      setSelectedPoolId('');
      await loadPools();
    } catch (err) {
      setError('Sammlung konnte nicht gelöscht werden.');
    }
  };

  const requirePool = () => {
    if (!selectedPoolId) {
      setError('Bitte zuerst eine Sammlung wählen oder erstellen.');
      return false;
    }
    return true;
  };

  const handleImport = async () => {
    if (!playlistUrls.trim() || !requirePool()) return;

    // Extract playlist IDs from URLs
    const lines = playlistUrls.split('\n').map(l => l.trim()).filter(Boolean);
    const playlistIds = lines.map(url => {
      const match = url.match(/playlist\/([a-zA-Z0-9]+)/);
      return match ? match[1] : url;
    });

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const res = await fetch(`${API_BASE}/spotify/import-playlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accessToken, playlistIds, poolId: selectedPoolId })
      });

      const data = await res.json();
      if (res.status === 401) {
        setError('Token abgelaufen. Bitte erneut einloggen.');
        setAccessToken(null);
      } else if (!res.ok) {
        setError(data.error || 'Import fehlgeschlagen');
      } else {
        setResult(data);
        loadPools();
      }
    } catch (err) {
      setError('Verbindungsfehler');
    }

    setLoading(false);
  };

  const handleImportAll = async () => {
    if (!requirePool()) return;
    setLoading(true);
    setError('');
    setResult(null);

    try {
      const res = await fetch(`${API_BASE}/spotify/import-my-playlists`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accessToken, poolId: selectedPoolId })
      });

      const data = await res.json();
      if (res.status === 401) {
        setError('Token abgelaufen. Bitte erneut einloggen.');
        setAccessToken(null);
      } else if (!res.ok) {
        setError(data.error || 'Import fehlgeschlagen');
      } else {
        setResult(data);
        loadPools();
      }
    } catch (err) {
      setError('Verbindungsfehler');
    }

    setLoading(false);
  };

  const handleImportArtists = async () => {
    if (!artistNames.trim() || !requirePool()) return;
    const artists = artistNames.split('\n').map(l => l.trim()).filter(Boolean);

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const res = await fetch(`${API_BASE}/spotify/import-artists`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ artists, poolId: selectedPoolId })
      });

      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Import fehlgeschlagen');
      } else {
        setResult(data);
        loadPools();
      }
    } catch (err) {
      setError('Verbindungsfehler');
    }

    setLoading(false);
  };

  const inputStyle = {
    width: '100%', padding: '12px', borderRadius: '8px',
    border: '1px solid var(--border)', background: 'var(--surface)',
    color: 'var(--text)', fontSize: '0.9rem'
  };

  return (
    <div className="solo-page">
      <div className="solo-container">
        <h1 className="page-title">
          <span className="back-btn" onClick={() => navigate('/')}>←</span>
          Playlist Import
        </h1>

        {/* Collection selector - applies to every import on this page */}
        <div style={{ padding: '1rem', background: 'var(--surface-alt, #1a1a1a)', borderRadius: '10px', marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
            🎧 Ziel-Sammlung
          </label>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginBottom: '0.75rem' }}>
            Alle Importe unten landen in dieser Sammlung.
          </p>

          {pools.length > 0 && (
            <select
              value={selectedPoolId}
              onChange={(e) => setSelectedPoolId(e.target.value)}
              style={{ ...inputStyle, marginBottom: '0.75rem' }}
            >
              <option value="">— Sammlung wählen —</option>
              {pools.map(p => (
                <option key={p.id} value={p.id}>{p.name} ({p.count || 0} Songs)</option>
              ))}
            </select>
          )}

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <input
              value={newPoolName}
              onChange={(e) => setNewPoolName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreatePool()}
              placeholder="Neue Sammlung erstellen (z.B. Rap, 90er, Party)..."
              style={{ ...inputStyle, flex: 1 }}
            />
            <button
              className="btn btn-primary"
              onClick={handleCreatePool}
              disabled={!newPoolName.trim()}
              style={{ whiteSpace: 'nowrap' }}
            >
              ＋ Erstellen
            </button>
          </div>

          {selectedPoolId && (
            <button
              className="btn btn-ghost btn-small"
              onClick={handleDeletePool}
              style={{ marginTop: '0.5rem', color: 'var(--danger, #e74c3c)' }}
            >
              🗑️ Diese Sammlung löschen
            </button>
          )}
        </div>

        {!accessToken ? (
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <p style={{ marginBottom: '1rem', color: 'var(--text-muted)' }}>
              Melde dich mit Spotify an um Playlists zu importieren.
            </p>
            <button className="btn btn-primary btn-large" onClick={handleLogin}>
              🎵 Mit Spotify einloggen
            </button>
          </div>
        ) : (
          <div style={{ marginTop: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <p style={{ color: 'var(--text-success, #4caf50)', margin: 0 }}>
                ✓ Eingeloggt{userInfo ? ` als ${userInfo.user}` : ''}
              </p>
              <button
                className="btn"
                onClick={() => {
                  setAccessToken(null);
                  setUserInfo(null);
                  window.open('https://accounts.spotify.com/logout', '_blank');
                }}
                style={{ fontSize: '0.8rem', padding: '4px 12px' }}
              >
                Abmelden
              </button>
            </div>
            {userInfo && userInfo.playlists && (
              <div style={{ marginBottom: '1rem', padding: '0.5rem', background: 'var(--surface-alt, #222)', borderRadius: '6px', fontSize: '0.85rem' }}>
                <p style={{ color: 'var(--text-muted)', marginBottom: '0.3rem' }}>Deine Playlists ({userInfo.playlistCount}):</p>
                {userInfo.playlists.map(p => (
                  <div key={p.id} style={{ cursor: 'pointer', padding: '2px 0', color: 'var(--text)' }}
                    onClick={() => setPlaylistUrls(prev => prev ? prev + '\n' + p.id : p.id)}>
                    📋 {p.name} ({p.tracks} Songs)
                  </div>
                ))}
              </div>
            )}
            <button
              className="btn btn-primary btn-large"
              onClick={handleImportAll}
              disabled={loading || !selectedPoolId}
              style={{ marginTop: '1rem', width: '100%' }}
            >
              {loading ? '⏳ Importiere...' : '🎵 Alle meine Playlists + Liked Songs importieren'}
            </button>
            <hr style={{ margin: '1.5rem 0', borderColor: 'var(--border)' }} />
            <p style={{ color: 'var(--text-muted)', marginBottom: '0.5rem' }}>Oder einzelne Playlist-URLs (eine pro Zeile):</p>
            <textarea
              value={playlistUrls}
              onChange={(e) => setPlaylistUrls(e.target.value)}
              placeholder={'https://open.spotify.com/playlist/...\nhttps://open.spotify.com/playlist/...\nhttps://open.spotify.com/playlist/...'}
              style={{ ...inputStyle, minHeight: '150px', resize: 'vertical' }}
            />
            <button
              className="btn btn-primary btn-large"
              onClick={handleImport}
              disabled={loading || !playlistUrls.trim() || !selectedPoolId}
              style={{ marginTop: '1rem', width: '100%' }}
            >
              {loading ? '⏳ Importiere...' : '📥 Playlists importieren'}
            </button>
          </div>
        )}

        {error && (
          <div className="error-message" style={{ marginTop: '1rem' }}>{error}</div>
        )}

        {result && (
          <div style={{ marginTop: '1rem', padding: '1rem', background: 'var(--surface)', borderRadius: '8px' }}>
            <h3 style={{ color: 'var(--text-success, #4caf50)' }}>✓ Import erfolgreich!</h3>
            <p>Neue Songs: <strong>{result.totalAdded}</strong></p>
            <p>Gesamt in Sammlung: <strong>{result.total}</strong></p>
            {result.results && result.results.map((r, i) => (
              <p key={i} style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                {r.name || r.artist || `Playlist ${(r.playlistId || '').slice(0, 8)}...`}: +{r.added} Songs
                {r.error && <span style={{ color: 'red' }}> ({r.error})</span>}
              </p>
            ))}
          </div>
        )}

        <hr style={{ margin: '2rem 0', borderColor: 'var(--border)' }} />

        <div>
          <h2 style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>🎤 Import nach Künstler</h2>
          <p style={{ color: 'var(--text-muted)', marginBottom: '0.5rem', fontSize: '0.85rem' }}>
            Kein Login nötig! Gib Künstlernamen ein (einen pro Zeile):
          </p>
          <textarea
            value={artistNames}
            onChange={(e) => setArtistNames(e.target.value)}
            placeholder={'Drake\nTravis Scott\nPlaYboi Carti\nKen Carson\n...'}
            style={{ ...inputStyle, minHeight: '120px', resize: 'vertical' }}
          />
          <button
            className="btn btn-primary btn-large"
            onClick={handleImportArtists}
            disabled={loading || !artistNames.trim() || !selectedPoolId}
            style={{ marginTop: '1rem', width: '100%' }}
          >
            {loading ? '⏳ Importiere...' : '🎤 Songs nach Künstler importieren'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Import;
